import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  FullCommissionData,
  NormalCommissionData,
  PaymentHistoryData,
  SubscriptionCommissionsData,
} from "@/types";
import { toast } from "react-toastify";
import {
  fetchFullCommission,
  fetchNormalCommmission,
  fetchPaymentsHistoryCommmission,
  fetchsubscriptionCommission,
  normalCommissionSettlement,
  subscriptionCommissionSettlement,
} from "./thunks";

interface PaginatedFullCommission {
  data: FullCommissionData[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface PaginatedPaymentHistory {
  data: PaymentHistoryData[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface PaginatedNormalCommission {
  data: NormalCommissionData[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface PaginatedSubscriptionCommission {
  data: SubscriptionCommissionsData[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface FullCommissionState {
  fullCommission: PaginatedFullCommission;
  paymentHistoryCommission: PaginatedPaymentHistory;
  normalCommission: PaginatedNormalCommission;
  subscriptionCommission: PaginatedSubscriptionCommission;
  loading: boolean;
  error: string | null;
}

const initialState: FullCommissionState = {
  fullCommission: { data: [] },
  paymentHistoryCommission: { data: [] },
  normalCommission: { data: [] },
  subscriptionCommission: { data: [] },
  loading: false,
  error: null,
};

const FullCommissionSlice = createSlice({
  name: "fullCommission",
  initialState,
  reducers: {
    clearFullCommission: (state) => {
      state.fullCommission = { data: [] };
      state.paymentHistoryCommission = { data: [] };
      state.normalCommission = { data: [] };
      state.subscriptionCommission = { data: [] };
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchFullCommission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchFullCommission.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const payload = action.payload;

          if (payload.totals && payload.compositions) {
            state.fullCommission = {
              data: [payload],
            };
          }
          // Se vier paginado
          else if (Array.isArray(payload.data)) {
            // Dados não paginados
            state.fullCommission = { data: payload.data };
          } else {
            // Dados paginados
            state.fullCommission = {
              data: payload.data.data,
              total: payload.data.total,
              currentPage: payload.data.current_page,
              perPage: payload.data.per_page,
            };
          }
        },
      )
      .addCase(fetchFullCommission.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      })

      // Payment History Commission
      .addCase(fetchPaymentsHistoryCommmission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchPaymentsHistoryCommmission.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const payload = action.payload;

          if (Array.isArray(payload.data)) {
            // Dados não paginados
            state.paymentHistoryCommission = { data: payload.data };
          } else {
            // Dados paginados
            state.paymentHistoryCommission = {
              data: payload.data.data,
              total: payload.data.total,
              currentPage: payload.data.current_page,
              perPage: payload.data.per_page,
            };
          }
        },
      )
      .addCase(fetchPaymentsHistoryCommmission.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      })

      // Normal Commission
      .addCase(fetchNormalCommmission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchNormalCommmission.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const payload = action.payload;

          state.normalCommission = { data: payload.data };
        },
      )
      .addCase(fetchNormalCommmission.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      })

      // Normal Commission Settlement
      .addCase(normalCommissionSettlement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        normalCommissionSettlement.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          toast.success("Baixa de comissão normal registrada com sucesso!");
        },
      )
      .addCase(normalCommissionSettlement.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as {
          error?: string;
          message?: string;
        };
        const errMsg =
          payload?.error ||
          payload?.message ||
          "Erro ao registrar a baixa de comissão";
        state.error = errMsg;
        toast.error(errMsg);
      })

      // Subscription Commissio
      .addCase(fetchsubscriptionCommission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchsubscriptionCommission.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const payload = action.payload;

          if (Array.isArray(payload.data)) {
            // Dados não paginados
            state.subscriptionCommission = { data: payload.data };
          } else {
            // Dados paginados
            state.subscriptionCommission = {
              data: payload.data,
              total: payload.data.total,
              currentPage: payload.data.current_page,
              perPage: payload.data.per_page,
            };
          }
        },
      )
      .addCase(fetchsubscriptionCommission.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      })

      // Normal Commission Settlement
      .addCase(subscriptionCommissionSettlement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        subscriptionCommissionSettlement.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          toast.success(
            "Baixa de comissão de assinatura registrada com sucesso!",
          );
        },
      )
      .addCase(subscriptionCommissionSettlement.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as {
          error?: string;
          message?: string;
        };
        const errMsg =
          payload?.error ||
          payload?.message ||
          "Erro ao registrar a baixa de comissão";
        state.error = errMsg;
        toast.error(errMsg);
      });
  },
});

export const { clearFullCommission } = FullCommissionSlice.actions;
export default FullCommissionSlice.reducer;
